import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/store/authStore';
import xmppService from '@/features/messages/xmppService';
import { useMessageUnreadStore } from '@/features/messages/messageUnreadStore';

/**
 * Keep realtime chat alive while the user is elsewhere in the app.
 * The full Messages screen owns richer callbacks when it is mounted, so this
 * lightweight connection is enabled only outside that route.
 */
export function useBackgroundChatConnection(enabled: boolean): void {
  const userId = useAuthStore((state) => state.user?.id) ?? '';
  const incrementConversationUnread = useMessageUnreadStore((state) => state.incrementConversation);
  const handledIdsRef = useRef(new Set<string>());

  // This hook lives at app-shell scope. Disconnect only when the authenticated
  // shell unmounts or the user changes/logs out—not when navigating routes.
  useEffect(() => {
    if (!userId) {
      xmppService.disconnect();
      return;
    }
    return () => xmppService.disconnect();
  }, [userId]);

  useEffect(() => {
    if (!enabled || !userId) return;

    const handleIncoming = (id: string | null, conversationId: number, isIncoming: boolean) => {
      if (!isIncoming) return;
      if (!Number.isFinite(conversationId) || conversationId <= 0) return;
      if (id) {
        if (handledIdsRef.current.has(id)) return;
        handledIdsRef.current.add(id);
        if (handledIdsRef.current.size > 500) {
          const oldest = handledIdsRef.current.values().next().value;
          if (oldest) handledIdsRef.current.delete(oldest);
        }
      }
      incrementConversationUnread(conversationId);
    };

    // Defer for React Strict Mode's development setup/cleanup cycle.
    const timer = window.setTimeout(() => {
      const credential = String(userId);
      xmppService.connect(credential, credential, {
        onMessage: (message) =>
          handleIncoming(
            message.id,
            Number((message.from || '').split('@')[0]),
            message.sender === 'incoming',
          ),
        onGroupMessage: (message) =>
          handleIncoming(message.id, Number(message.groupId), !message.isOwn),
        onDisconnected: (reason) => console.log('[chat] Background XMPP disconnected', reason),
      });
    }, 0);

    return () => {
      window.clearTimeout(timer);
    };
  }, [enabled, incrementConversationUnread, userId]);
}
